import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const ThemeContext = createContext(null);
export const useTheme = () => useContext(ThemeContext);

// One light/dark choice for the whole product: public site, login, client portal (/portal),
// admin (/admin) and the Ops console (/ops).
// The choice is saved in localStorage AND in a cookie on the parent domain, so a choice made on
// the public site (mentoraicapital.com) carries to the dashboard (app.mentoraicapital.com) and back.
// Until someone has chosen, the Ops console keeps its original dark look and every other page is light.
// public/index.html applies the saved choice before React loads, so a reload never flashes the wrong theme.
const KEY = "mentorai_theme";
const valid = (v) => (v === "dark" || v === "light" ? v : null);

const cookieDomain = () => {
  const h = window.location.hostname;
  return h === "mentoraicapital.com" || h.endsWith(".mentoraicapital.com") ? "; domain=.mentoraicapital.com" : "";
};

const readCookie = () => {
  const m = document.cookie.match(/(?:^|;\s*)mentorai_theme=(dark|light)/);
  return m ? m[1] : null;
};

const readStored = () => {
  // The cookie is shared across the site and the app subdomain, so it holds the latest choice.
  let ls = null;
  try { ls = valid(localStorage.getItem(KEY)); } catch (e) { /* storage blocked */ }
  return readCookie() || ls;
};

const saveChoice = (v) => {
  try { localStorage.setItem(KEY, v); } catch (e) { /* storage blocked */ }
  const secure = window.location.protocol === "https:" ? "; secure" : "";
  document.cookie = `${KEY}=${v}; path=/; max-age=31536000; samesite=lax${cookieDomain()}${secure}`;
};

export const ThemeProvider = ({ children }) => {
  const [stored, setStored] = useState(readStored);
  const location = useLocation();
  const path = location.pathname;
  const theme = stored || (path.startsWith("/ops") ? "dark" : "light");

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    root.style.colorScheme = theme;
  }, [theme]);

  // A choice made in another tab (or on the other subdomain) is picked up when this tab regains focus.
  useEffect(() => {
    const sync = () => {
      const v = readStored();
      if (v) setStored((cur) => (cur === v ? cur : v));
    };
    window.addEventListener("focus", sync);
    window.addEventListener("storage", sync);
    return () => { window.removeEventListener("focus", sync); window.removeEventListener("storage", sync); };
  }, []);

  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    saveChoice(next);
    setStored(next);
  };

  return <ThemeContext.Provider value={{ theme, toggle }}>{children}</ThemeContext.Provider>;
};
